import { useMemo, useState } from 'react'
import { Button, RadioGroup } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useTheme } from '@itsrever/design-system'
import type { ReturnsProductDetailsResponse } from '@itsrever/rever-api'
import { QuantitySelector } from '@/ui/components/quantity-selector'
import { ProductImages } from './product-images'
import { ProductVariantButton } from './product-variant-button'
import { getGridPosition } from '@/components/FullCatalog/utils'
import { usePostHog } from '@/application/use-posthog'
import { useAppSelector } from '@/redux/hooks'
import { useCart } from '@/application/use-cart/use-cart'
import { ProductDescription } from './product-description'
import { hasVariantStock } from '../helpers/has-variant-stock'

interface ProductDetailsProps {
    product: ReturnsProductDetailsResponse
    categoryIndex: number
    returnedItemId: string
    onClose: () => void
}

export const ProductDetails = ({
    product: { product_info, signed_product_raw },
    categoryIndex,
    returnedItemId,
    onClose
}: ProductDetailsProps) => {
    const { t } = useTranslation()

    const { captureEventPosthog } = usePostHog()

    const theme = useTheme()

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const { addItemToCart } = useCart()

    const [activeImage, setActiveImage] = useState<string | undefined>(
        product_info?.featured_image_url
    )
    const [{ quantity, variant }, setSelected] = useState({
        variant: product_info?.variants?.[0],
        quantity: 1
    })

    const maxQuantity =
        variant && hasVariantStock(variant) ? variant.inventory_quantity : 1

    const images = useMemo(() => {
        const productVariants = product_info?.variants ?? []
        const productImages = product_info?.image_urls ?? []
        const variantsImages = productVariants.map(
            (variant) => variant.image_urls?.[0]
        )

        return Array.from(new Set([...productImages, ...variantsImages]))
    }, [product_info?.image_urls, product_info?.variants])

    const onVariantSelect = (variantId: string) => {
        const variant = product_info?.variants?.find(
            (variant) => variant.platform_id === variantId
        )

        setSelected({ variant, quantity: 1 })
        variant?.image_urls?.[0] && setActiveImage(variant?.image_urls?.[0])
    }

    const addToCart = () => {
        const productId = product_info?.platform_id

        if (productId && variant) {
            const gridPosition = getGridPosition(productId)

            captureEventPosthog('FC - Product added to cart', {
                row: gridPosition?.row,
                col: gridPosition?.col,
                categoryIndex,
                ecommerceId: settings.ecommerce_id
            })

            variant &&
                addItemToCart(returnedItemId, quantity, variant, {
                    product_info,
                    signed_product_raw
                })

            onClose()
        }
    }

    const color = theme.colors.primary.main
    const textColor = theme.colors.primary.contrastText
    const rounded = theme.borderRadius

    return (
        <div className="flex w-full items-center justify-center">
            <div className="flex h-[80vh] max-h-[615px] w-full gap-10 p-4 pb-10 pt-12 xl:px-12 2xl:max-w-7xl">
                <ProductImages
                    images={images}
                    selectedImage={activeImage}
                    onSelectImage={setActiveImage}
                />
                <div className="flex flex-1 flex-col">
                    <h1 className="pb-4 font-roobert text-2xl font-normal">
                        {product_info?.name}
                    </h1>
                    {product_info?.description ? (
                        <div className="pb-6">
                            <ProductDescription
                                description={product_info.description}
                            />
                        </div>
                    ) : null}
                    <div className="flex flex-col gap-5 pb-4">
                        <p id="variant-options">
                            {t('full_catalog.select_option')}
                        </p>
                    </div>
                    <div className="h-full overflow-auto pr-1.5">
                        <RadioGroup
                            aria-labelledby="variant-options"
                            onValueChange={onVariantSelect}
                            value={variant?.platform_id}
                            orientation="vertical"
                        >
                            {product_info?.variants?.map((variant) => (
                                <ProductVariantButton
                                    value={variant.platform_id ?? ''}
                                    key={variant.platform_id}
                                    variant={variant}
                                />
                            ))}
                        </RadioGroup>
                    </div>
                    <div className="mt-auto flex items-center justify-center gap-6 pt-4">
                        <QuantitySelector
                            value={quantity}
                            onValueChange={(q) =>
                                setSelected((prev) => ({
                                    ...prev,
                                    quantity: q
                                }))
                            }
                            minValue={1}
                            maxValue={maxQuantity}
                        />
                        <Button
                            id="confirm-variant-button"
                            variant={
                                color === 'transparent' ? 'light' : 'solid'
                            }
                            radius="sm"
                            size={color === 'transparent' ? 'md' : 'lg'}
                            fullWidth
                            disabled={
                                variant ? !hasVariantStock(variant) : true
                            }
                            data-testid="continue"
                            className={clsx('disabled:opacity-60', {
                                'underline underline-offset-2 hover:!bg-neutral-100':
                                    color === 'transparent'
                            })}
                            style={{
                                width: '100%',
                                maxWidth: '20rem',
                                backgroundColor:
                                    color === 'transparent'
                                        ? 'transparent'
                                        : color,
                                color: textColor,
                                borderRadius: rounded
                            }}
                            onPress={addToCart}
                        >
                            {t('full_catalog.add_to_cart')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
